<template>
  <DefaultLayout :disableFacilitatorTools="true">
    <div class="text-center">
      <h4>{{ $t("title") }}</h4>
    </div>

    <b-toast
      id="save-successful-toast"
      no-close-button
      b-toaster-top-right
      variant="success"
      :solid="true"
    >
      <strong>
        {{ $t("save-successful") }}
      </strong>
    </b-toast>

    <b-toast
      id="file-too-big-toast"
      no-close-button
      b-toaster-top-right
      variant="danger"
      :solid="true"
    >
      <strong>
        {{ $t("file-too-big") }}
      </strong>
    </b-toast>

    <div v-if="ready">
      {{ $t("file-specs") }}

      <div v-if="rpsScenarios[0]">
        <br />
        <br />
        {{ $t("lobby-photo") }}      
        <div v-if="!rpsScenarios[0].lobby_photo">
          <small>{{ $t("no-photo-yet") }}</small>
          <br />
        </div>
        <div v-else>
          <img v-lazy="getLobbyPhoto()" class="img-fluid" />
        </div>
        {{ $t("change-lobby") }}
        <b-form-file
          ref="lobby-photo-input"      
          :accept="fileTypes"
          @input="setLobbyPhoto($event)"
          v-model="lobbyPhoto"
        />

        <br />
        <hr/>
        <br />

        {{ $t("start-photo") }}
        <div v-if="!rpsScenarios[0].start_photo">
          <small>{{ $t("no-photo-yet") }}</small>
          <br />
        </div>
        <div v-else>
          <img v-lazy="getStartPhoto()" class="img-fluid" />
        </div>
        {{ $t("change-start") }}
        <b-form-file
          ref="start-photo-input"      
          :accept="fileTypes"
          @input="setStartPhoto($event)"
          v-model="startPhoto"
        />

        <br />
        <hr/>

        <div v-for="aCase in getRPSCases" :key="aCase.case_number">
          {{ $t("file-specs") }}
          {{ $t("case") }} {{ aCase.case_number }} {{ aCase.title }}
          <br />
          <div v-if="!aCase.photo">
            <small>{{ $t("no-photo-yet") }}</small>
            <br />
          </div>
          <div v-else>
            <img v-lazy="getCasePhoto(aCase)" class="img-fluid" />
          </div>
          {{ $t("change-case") }}
          <br />
          <b-form-file
            :ref="'case-photo-input-' + aCase.case_number"       
            :accept="fileTypes"
            @input="setCasePhoto($event, aCase.case_number)"
            v-model="casePhotos[aCase.case_number]"
          ></b-form-file>
          <br /> 
          <hr/>
        </div>

        {{ $t("completion-photo") }}
        <div v-if="!rpsScenarios[0].completion_photo">
          <small>{{ $t("no-photo-yet") }}</small>
          <br />
        </div>
        <div v-else>
          <img v-lazy="getCompletionPhoto()" class="img-fluid" />
        </div>
        {{ $t("change-completion") }}
        <b-form-file
          ref="completion-photo-input"      
          :accept="fileTypes"
          @input="setCompletionPhoto($event)"
          v-model="completionPhoto"
        />
      </div>

      <br/>
      <hr/>

      <base-alert type="danger" v-if="saveError">
      <span class="alert-inner--icon"><i class="ni ni-notification-70"></i></span>
      <span class="alert-inner--text">{{ $t('save-error') }} {{ saveErrorMessage }}</span>
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
      </button>
    </base-alert>

      <base-button
        type="primary"
        :disabled="disableSave"
        style="white-space: normal; margin: 2px"
        v-on:click="save()"
        >{{ $t("save") }}
      </base-button>

      <base-button
        type="primary" size="sm"
        style="white-space: normal; margin: 2px"
        v-on:click="$router.push('/admin/editor')"
        >{{ $t(exitButtonText) }}
      </base-button>
    </div>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout.vue";

import { getRPSCases, getRPSScenario, sendScenarioPhoto, sendCasePhoto } from "@/services/APIService";

import { convertPhotoToAmazonURL } from "@/services/utils";

const cloneDeep = require("lodash.clonedeep");
const isEqual = require("lodash.isequal");
const sortby = require("lodash.sortby");

export default {
  components: {
    DefaultLayout,
  },
  data() {
    return {
      rpsScenarioId: null,
      rpsScenarios: [],
      rpsCases: [],
      casePhotos: [],
      startPhoto: null,
      completionPhoto: null,
      lobbyPhoto: null,
      saveError: false,
      saveErrorMessage: null,
      originalStartPhoto: null,
      originalCasePhotos: [],
      originalCompletionPhoto: null,
      originalLobbyPhoto: null,
      ready: false,
    };
  },
  computed: {
    exitButtonText() {
      if (this.disableSave) {
        return "exit";
      } else {
        return "exit-discard";
      }
    },    
    getRPSCases() {
      const cases = [];

      for (let i = 0; i < this.rpsCases.length; i++) {
        if (this.rpsCases[i].locale === this.$i18n.locale) {
          cases.push(this.rpsCases[i]);
        }
      }

      if (cases.length === 0) {
        // Ooops, not available in selected locale :-/ Let's find another one
        const locale = this.rpsCases[0].locale;

        for (let i = 0; i < this.rpsCases.length; i++) {
          if (this.rpsCases[i].locale === locale) {
            cases.push(this.rpsCases[i]);
          }
        }
      }

      return sortby(cases, [
        (o) => {
          return o.case_number;
        },
      ]);
    },
    disableSave() {
      if (this.casePhotos.length > 0) {
        return false;
      }

      if (this.startPhoto === null && this.lobbyPhoto === null && this.completionPhoto === null) {
        return true;
      } else {
        return false;
      }
    },
    fileTypes() {
      return "image/png, image/jpeg";
    },
  },
  async mounted() {
    this.rpsScenarioId = this.$route.params.rpsscenarioid;
    await this.getData();
    this.copyOriginalPhotos();
    this.ready = true;
  },
  methods: {
    copyOriginalPhotos() {
      this.originalStartPhoto = this.startPhoto;
      this.originalLobbyPhoto = this.lobbyPhoto;
      this.originalCompletionPhoto = this.completionPhoto;
      this.originalCasePhotos = cloneDeep(this.originalCasePhotos)
    },
    async getData() {
      this.rpsScenarios = (await getRPSScenario(this.rpsScenarioId)).data;

      this.rpsCases = (await getRPSCases(this.rpsScenarioId)).data;
    },
    getCasePhoto(theCase) {
      return convertPhotoToAmazonURL(theCase.photo);
    },
    getStartPhoto() {
      return convertPhotoToAmazonURL(this.rpsScenarios[0].start_photo);
    },
    getCompletionPhoto() {
      return convertPhotoToAmazonURL(this.rpsScenarios[0].completion_photo);
    },
    getLobbyPhoto() {
      return convertPhotoToAmazonURL(this.rpsScenarios[0].lobby_photo);
    },
    isPhotoTooBig(file) {
      if (file.size > 512 * 1024) {
        this.$bvToast.show("file-too-big-toast");
        return true;
      } else {
        return false;
      }
    },
    async submitFiles() {
      try {
        if (this.lobbyPhoto) {
          await sendScenarioPhoto(
            this.rpsScenarioId,
            "lobby_photo",
            this.lobbyPhoto
          );
        }

        if (this.startPhoto) {
          await sendScenarioPhoto(
            this.rpsScenarioId,
            "start_photo",
            this.startPhoto
          );
        }

        if (this.completionPhoto) {
          await sendScenarioPhoto(
            this.rpsScenarioId,
            "completion_photo",
            this.completionPhoto
          );
        }

        for (let i = 0; i < this.casePhotos.length; i++) {
          if (this.casePhotos[i]) {
            await sendCasePhoto(this.rpsScenarioId, i, this.casePhotos[i]);
          }
        }

        this.casePhotos = [];
        this.startPhoto = null;
        this.lobbyPhoto = null;
        this.completionPhoto = null;
        this.getData();
        this.saveError = false;
        this.$bvToast.show("save-successful-toast");
        this.copyOriginalPhotos();
      } catch (error) {
        this.saveError = true;
        this.saveErrorMessage = error.message;
      }
    },
    setCasePhoto(file, caseNumber) {
      if (!file) {
        return;
      }

      if (this.isPhotoTooBig(file)) {
        // Alert
        this.casePhotos[caseNumber] = null;
        const refName = "case-photo-input-" + caseNumber; 
        this.$refs[refName][0].reset();
      }
    },
    setStartPhoto(file) {
      if (!file) {
        return;
      }
      if (this.isPhotoTooBig(file)) {
        this.startPhoto = null;
        this.$refs['start-photo-input'].reset()
      }
    },
    setCompletionPhoto(file) {
      if (!file) {
        return;
      }
      if (this.isPhotoTooBig(file)) {
        this.completionPhoto = null;
        this.$refs['completion-photo-input'].reset()
      }
    },
    setLobbyPhoto(file) {
      if (!file) {
        return;
      }

      if (this.isPhotoTooBig(file)) {
        this.lobbyPhoto = null;
        this.$refs['lobby-photo-input'].reset()        
      }
    },
    async save() {
      await this.submitFiles();
    },
  },
};
</script>

<i18n>
  {
    "en": {
      "case": "Case",
      "no-photo-yet": "No photo yet",
      "change-case": "Change case photo",
      "title": "Simulation photo editor",
      "exit-discard": "Exit and discard changes",
      "exit": "Exit",
      "save": "Save changes",
      "file-specs": "Please crop your photos to resolution 1600x300 pixels. You can use JPG or PNG photos. Maximum size for one photo is 500 kBs.",
      "start-photo": "Start photo, shown in the simulation introduction before the first case",
      "change-start": "Change start photo",
      "lobby-photo": "Lobby photo, shown when players are waiting for the simulation to start",
      "change-lobby": "Change lobby photo",
      "completion-photo": "Completion photo, shown after the last case, before reports are opened",      
      "change-completion": "Change completion photo",
      "file-too-big": "File is too big (over 500 kBs)",
      "save-error": "Error saving photos! Please contact SmoothTeam support at smoothteam@smoothteam.net. Error message:",
      "save-successful": "Save successful"
    },
    "fi": {
      "case": "Tilanne",
      "no-photo-yet": "Ei vielä kuvaa",
      "change-case": "Valitse kuva tilanteeseen",
      "title": "Simulaation kuvaeditori",
      "exit-discard": "Poistu ja peru muutokset",      
      "exit": "Poistu",
      "save": "Tallenna muutokset",
      "file-specs": "Suositeltu kuvan koko on 1600x300 pikseliä. Voit käyttää JPG- tai PNG-kuvia. Yhden kuvan enimmäiskoko on 500 kilotavua.",
      "start-photo": "Aloituskuva, näytetään simulaation aloitussivulla ennen ensimmäistä tilannetta",
      "change-start": "Vaihda aloituskuva",
      "lobby-photo": "Aulakuva, näytetään kun pelaajat odottavat simulaation avaamista",
      "change-lobby": "Vaihda aulakuva",
      "completion-photo": "Lopetuskuva, näytetään viimeisen tilanteen jälkeen ennen kuin raportit avataan",      
      "change-completion": "Vaihda lopetuskuva",
      "file-too-big": "Liian suuri kuva (yli 500 kilotavua)",
      "save-error": "Kuvien tallennus epäonnistui! Ota yhteyttä SmoothTeamin tukeen smoothteam@smoothteam.fi. Virheilmoitus:",
      "save-successful": "Tallennus onnistui"
    }
  }
  </i18n>
